import { motion, useReducedMotion } from "framer-motion";
import { DataKeys } from "../../../types";
import { getHealthProductPreviewSrc } from "../healthProductPreviewAssets";
import { getLocalized, HealthLang, LocalizedString } from "../healthTypes";
import {
    healthChildPageHeroBackdropFadeDesktop,
    healthChildPageHeroBackdropFadeMobile,
    healthChildPageHeroBackdropImage,
    healthChildPageHeroBackdropWrap,
} from "../healthVisualStyles";

const HERO_BACKDROP_EASE = [0.22, 1, 0.36, 1] as const;

function useHeroBackdropMotion(delay = 0.1) {
    const reduceMotion = useReducedMotion();

    if (reduceMotion) {
        return {
            initial: false as const,
            animate: { opacity: 1, x: 0 },
            transition: { duration: 0 },
        };
    }

    return {
        initial: { opacity: 0, x: 16 },
        animate: { opacity: 1, x: 0 },
        transition: { duration: 0.7, delay, ease: HERO_BACKDROP_EASE },
    };
}

interface HealthChildPageHeroBackdropProps {
    visual: DataKeys;
    lang: HealthLang;
}

function useHeroBackdropSrc(visual: DataKeys) {
    const assetKey = visual.assetKey as string | undefined;
    return getHealthProductPreviewSrc(assetKey);
}

/** Lifestyle backdrop behind hero copy — right-weighted on desktop, full-bleed on mobile. */
export function HealthChildPageHeroBackdropLayer({
    visual,
    lang,
}: HealthChildPageHeroBackdropProps) {
    const src = useHeroBackdropSrc(visual);
    const alt = getLocalized(visual.alt as LocalizedString, lang);
    const motionProps = useHeroBackdropMotion(0.12);

    if (!src) {
        return null;
    }

    return (
        <motion.div
            className={healthChildPageHeroBackdropWrap}
            aria-hidden="true"
            initial={motionProps.initial}
            animate={motionProps.animate}
            transition={motionProps.transition}
        >
            <img
                src={src}
                alt=""
                className={healthChildPageHeroBackdropImage}
                loading="eager"
                decoding="async"
            />
            <span className={healthChildPageHeroBackdropFadeMobile} />
            <span className={healthChildPageHeroBackdropFadeDesktop} />
            <span className="sr-only">{alt}</span>
        </motion.div>
    );
}
